// utils/dateHelpers.js
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';

// Locale mapping for supported languages
const LOCALE_MAP = {
  tr: tr,
  en: enUS,
  'en-US': enUS,
  'tr-TR': tr
};

// Default locale fallback
const DEFAULT_LOCALE = enUS;

/**
 * Get locale object based on language code
 * @param {string} languageCode - Language code (e.g., 'tr', 'en')
 * @returns {Object} date-fns locale object
 */
export const getDateLocale = (languageCode) => {
  if (!languageCode) return DEFAULT_LOCALE;
  
  // Handle full locale codes like 'tr-TR' or simple codes like 'tr'
  const normalizedCode = languageCode.toLowerCase();
  
  return LOCALE_MAP[normalizedCode] || 
         LOCALE_MAP[normalizedCode.split('-')[0]] || 
         DEFAULT_LOCALE;
};

/**
 * Format date with localization support
 * @param {string|Date} date - Date to format
 * @param {string} formatString - Format pattern (default: 'PPP')
 * @param {string} languageCode - Language code for localization
 * @param {string} fallbackText - Text to show if date is invalid
 * @returns {string} Formatted date string
 */
export const formatLocalizedDate = (date, formatString = 'PPP', languageCode = 'en', fallbackText = 'Not available') => {
  if (!date) return fallbackText;
  
  try {
    // Handle string dates (ISO format)
    const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
    
    // Validate date
    if (!isValid(dateObj)) {
      console.warn('Invalid date provided to formatLocalizedDate:', date);
      return fallbackText;
    }

    const locale = getDateLocale(languageCode);
    return format(dateObj, formatString, { locale });
  } catch (error) {
    console.error('Date formatting error:', error, { date, formatString, languageCode });
    return fallbackText;
  }
};

/**
 * Format date as relative time with localization (e.g., "2 hours ago")
 * @param {string|Date} date - Date to format
 * @param {string} languageCode - Language code for localization
 * @param {string} fallbackText - Text to show if date is invalid
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date, languageCode = 'en', fallbackText = 'Unknown') => {
  if (!date) return fallbackText;
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
    
    if (!isValid(dateObj)) {
      console.warn('Invalid date provided to formatRelativeTime:', date);
      return fallbackText;
    }

    const locale = getDateLocale(languageCode);
    return formatDistanceToNow(dateObj, { 
      addSuffix: true, 
      locale 
    });
  } catch (error) {
    console.error('Relative time formatting error:', error, { date, languageCode });
    return fallbackText;
  }
};

/**
 * Common date format presets
 */
export const DATE_FORMATS = {
  FULL: 'PPPP', // Monday, April 4th, 2022
  LONG: 'PPP',  // April 4th, 2022
  MEDIUM: 'PP', // Apr 4, 2022
  SHORT: 'P',   // 04/04/2022
  TIME: 'p',    // 9:41 AM
  DATE_TIME: 'Pp', // Apr 4, 2022, 9:41 AM
  FULL_DATE_TIME: 'PPPp', // April 4th, 2022 at 9:41 AM
  ISO: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx" // ISO format
};

/**
 * Create a hook-like function that can be used in React components
 * This function takes i18n instance and returns formatting functions
 * @param {Object} i18n - i18next instance
 * @returns {Object} Object with formatting functions
 */
export const createDateFormatters = (i18n) => {
  const currentLanguage = i18n?.language || 'en';
  
  return {
    formatDate: (date, formatString = DATE_FORMATS.LONG, fallbackText) => 
      formatLocalizedDate(date, formatString, currentLanguage, fallbackText),
    
    formatRelative: (date, fallbackText) => 
      formatRelativeTime(date, currentLanguage, fallbackText),
    
    formatShort: (date, fallbackText) => 
      formatLocalizedDate(date, DATE_FORMATS.SHORT, currentLanguage, fallbackText),
    
    formatLong: (date, fallbackText) => 
      formatLocalizedDate(date, DATE_FORMATS.LONG, currentLanguage, fallbackText),
    
    formatDateTime: (date, fallbackText) => 
      formatLocalizedDate(date, DATE_FORMATS.DATE_TIME, currentLanguage, fallbackText),
    
    formatTime: (date, fallbackText) => 
      formatLocalizedDate(date, DATE_FORMATS.TIME, currentLanguage, fallbackText)
  };
};

/**
 * React hook for date formatting (can be used in functional components)
 * @param {Object} i18n - i18next instance from useTranslation hook
 * @returns {Object} Object with formatting functions
 */
export const useDateFormatters = (i18n) => {
  return createDateFormatters(i18n);
};

/**
 * Format date in short format with dots (dd.MM.yyyy)
 * @param {string|Date} dateString - Date to format
 * @param {Object} options - Additional options
 * @returns {string} Formatted date (e.g., "15.03.2024")
 */
export const formatDateShort = (dateString, options = {}) => {
  if (!dateString) {
    return options.t?.('brandManagement.notProvided', 'Not provided') || 'Not provided';
  }
  
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
    
    // Check if date is valid
    if (!isValid(date)) {
      return options.t?.('brandManagement.notProvided', 'Not provided') || 'Not provided';
    }

    // Custom formatting for better control with dot separator
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (error) {
    console.warn('Date formatting error:', error);
    return options.t?.('brandManagement.notProvided', 'Not provided') || 'Not provided';
  }
};

/**
 * Format date in medium format with dots
 * @param {string|Date} dateString - Date to format
 * @param {Object} options - Additional options
 * @returns {string} Formatted date (e.g., "15.03.2024")
 */
export const formatDateMedium = (dateString, options = {}) => 
  formatDateShort(dateString, options);

/**
 * Format date in long format with month names
 * @param {string|Date} dateString - Date to format
 * @param {Object} options - Additional options
 * @returns {string} Formatted date (e.g., "15.Mart.2024")
 */
export const formatDateLong = (dateString, options = {}) => {
  if (!dateString) {
    return options.t?.('brandManagement.notProvided', 'Not provided') || 'Not provided';
  }
  
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
    
    if (!isValid(date)) {
      return options.t?.('brandManagement.notProvided', 'Not provided') || 'Not provided';
    }

    const locale = getDateLocale(options.locale || 'tr');
    return format(date, 'dd.MMMM.yyyy', { locale });
  } catch (error) {
    console.warn('Date formatting error:', error);
    return options.t?.('brandManagement.notProvided', 'Not provided') || 'Not provided';
  }
};

// Export commonly used formats for convenience
export { DATE_FORMATS as dateFormats };

/**
 * Legacy dateHelpers object for backward compatibility
 * @deprecated Use the new date formatting functions instead
 * This will be removed in a future version
 */
export const dateHelpers = {
  /**
   * Combine date and time string into a single Date object
   * @param {Date|string} date - Base date
   * @param {string} timeString - Time in HH:mm format
   * @returns {Date|null} Combined date object
   */
  combineDateTime: (date, timeString) => {
    if (!date) return null;
    
    try {
      const result = new Date(date);
      if (timeString && typeof timeString === 'string') {
        const [hours, minutes] = timeString.split(':').map(Number);
        if (!isNaN(hours) && !isNaN(minutes)) {
          result.setHours(hours, minutes, 0, 0);
        }
      }
      return isValid(result) ? result : null;
    } catch (error) {
      console.error('Error combining date and time:', error);
      return null;
    }
  },

  /**
   * Convert date to ISO string
   * @param {Date} date - Date to convert
   * @returns {string|null} ISO string or null
   */
  toISOString: (date) => {
    if (!date) return null;
    try {
      const dateObj = new Date(date);
      return isValid(dateObj) ? dateObj.toISOString() : null;
    } catch (error) {
      console.error('Error converting to ISO string:', error);
      return null;
    }
  },

  /**
   * Format date for display
   * @param {Date|string} date - Date to format
   * @returns {string} Formatted date string
   */
  formatDisplay: (date) => {
    if (!date) return '';
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
      if (!isValid(dateObj)) return '';
      
      // Use Turkish locale for backward compatibility
      const locale = getDateLocale('tr');
      return format(dateObj, 'dd MMMM yyyy HH:mm', { locale });
    } catch (error) {
      console.error('Error formatting date for display:', error);
      return '';
    }
  },

  /**
   * Get time from date object
   * @param {Date|string} date - Date object
   * @returns {string} Time in HH:mm format
   */
  getTimeFromDate: (date) => {
    if (!date) return '';
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
      if (!isValid(dateObj)) return '';
      return format(dateObj, 'HH:mm');
    } catch (error) {
      console.error('Error getting time from date:', error);
      return '';
    }
  }
};